import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT, mergeContent, imageUrl, withTrialDays } from "@/lib/siteContent";
import { Brand } from "@/components/Brand";
import { useBranding } from "@/lib/branding";
import { ArrowRight, ClipboardCheck, Thermometer, ShieldCheck, Wheat, AlertTriangle, FileText, Smartphone, Sparkles, Building2, Check } from "lucide-react";

const FEATURES = [
  { icon: ClipboardCheck, title: "Daily diary & checks", text: "Opening, service, cleaning and closing checks scheduled for every venue, with sign-off and overdue flags." },
  { icon: Thermometer, title: "Temperature logs", text: "Fridges, freezers, cooking, cooling, reheating and hot holding — with UK limits and instant out-of-range alerts." },
  { icon: AlertTriangle, title: "Incidents & corrective actions", text: "Every failure becomes a tracked incident: food disposition, maintenance link, manager review, safe-to-resume sign-off." },
  { icon: ShieldCheck, title: "AI HACCP plans", text: "Draft a full 7-principle HACCP plan from your menu in minutes. Version it, review it, and get staff to acknowledge it." },
  { icon: Wheat, title: "14-allergen matrix", text: "A live allergen matrix for every dish, ready for Natasha's Law questions at the pass." },
  { icon: FileText, title: "EHO-ready audit pack", text: "One click for checks, temperatures, incidents, training, deliveries and allergens for any date range." },
];

const MODULES = ["SFBB diary", "HACCP", "Allergens", "Temperatures", "Cleaning", "Equipment", "Training", "Suppliers", "Deliveries", "Incidents", "Audit trail", "Multi-site"];

export function Nav() {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-[#F6F7F4]/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 sm:gap-6 sm:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2"><Brand /></Link>
        <nav className="ml-6 hidden gap-6 text-sm text-slate-600 md:flex">
          <a href="/#features" className="hover:text-slate-900">Features</a>
          <Link data-testid="nav-tour-link" to="/tour" className="hover:text-slate-900">Product tour</Link>
          <a href="/#staff-app" className="hover:text-slate-900">Staff app</a>
          <a href="/#pricing" className="hover:text-slate-900">Pricing</a>
        </nav>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <Link data-testid="nav-tour-link-mobile" to="/tour" className="hidden whitespace-nowrap px-2 text-sm font-semibold text-slate-700 sm:inline md:hidden">Tour</Link>
          <Link data-testid="landing-login-link" to="/login" className="ka-press whitespace-nowrap rounded-full px-2 py-2 text-sm font-semibold text-slate-700 hover:bg-white sm:px-4">Sign in</Link>
          <Link data-testid="landing-signup-link" to="/signup" className="ka-press whitespace-nowrap rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 sm:px-5"><span className="sm:hidden">Free trial</span><span className="hidden sm:inline">Start free trial</span></Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ c }) {
  return (
    <section className="ka-grain relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:pt-24">
        <div className="ka-fade">
          <div className="ka-eyebrow mb-5 flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{c.hero_eyebrow}</div>
          <h1 data-testid="hero-title" className="text-4xl font-extrabold leading-[1.02] text-slate-950 sm:text-5xl lg:text-6xl">{c.hero_title_1}<br /><span className="text-emerald-700">{c.hero_title_2}</span></h1>
          <p className="mt-6 max-w-xl text-base text-slate-600 sm:text-lg">{c.hero_subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link data-testid="hero-signup-button" to="/signup" className="ka-press inline-flex items-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700">{c.hero_cta} <ArrowRight className="h-4 w-4" /></Link>
            <Link data-testid="hero-login-button" to="/login" className="ka-press inline-flex items-center rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 hover:border-slate-900">I have an account</Link>
            <Link data-testid="hero-tour-link" to="/tour" className="ka-press inline-flex items-center gap-1 px-2 py-3.5 text-sm font-semibold text-emerald-700 hover:underline">Take the product tour <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
            {c.hero_points.map((t) => <span key={t} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" />{t}</span>)}
          </div>
        </div>
        <div className="relative ka-fade" style={{ animationDelay: ".15s" }}>
          <img data-testid="hero-image" src={imageUrl(c.hero_image, "/img/hero.jpg")} alt="Chef working in a professional kitchen" className="h-[420px] w-full rounded-3xl object-cover lg:h-[520px]" />
          <div className="absolute -bottom-6 left-4 w-64 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:left-[-2rem]">
            <div className="ka-eyebrow">Walk-in fridge</div>
            <div className="mt-1 flex items-end justify-between"><span className="font-display text-3xl font-extrabold text-slate-900">3.2°C</span><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">In range</span></div>
            <div className="mt-2 text-xs text-slate-500">Recorded by Sam · 08:42</div>
          </div>
          <div className="absolute -top-4 right-4 rounded-2xl bg-slate-950 p-4 text-white shadow-xl">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Compliance today</div>
            <div className="font-display text-3xl font-extrabold text-emerald-400">92%</div>
          </div>
        </div>
      </div>
      <div className="border-y border-slate-200 bg-white/70">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-x-8 gap-y-2 px-5 py-4 font-mono text-xs uppercase tracking-widest text-slate-500 sm:px-8">
          {MODULES.map((m) => <span key={m}>{m}</span>)}
        </div>
      </div>
    </section>
  );
}

function Features({ c }) {
  return (
    <section id="features" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="max-w-2xl">
        <div className="ka-eyebrow mb-3">{c.features_eyebrow}</div>
        <h2 className="text-3xl font-extrabold text-slate-950 sm:text-4xl">{c.features_title}</h2>
        <p className="mt-4 text-slate-600">{c.features_text}</p>
      </div>
      <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {c.features.map((blk, i) => ({ ...FEATURES[i % FEATURES.length], ...blk })).map((f) => (
          <div key={f.title} className="group bg-white p-8 transition-colors duration-200 hover:bg-emerald-50/50">
            <f.icon className="h-6 w-6 text-emerald-600 transition-transform duration-200 group-hover:-translate-y-0.5" />
            <h3 className="mt-5 text-xl font-bold text-slate-900">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StaffApp({ c }) {
  return (
    <section id="staff-app" className="bg-slate-950 text-white">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2">
        <img data-testid="staff-image" src={imageUrl(c.staff_image, "/img/staff.jpg")} alt="Kitchen team at work" className="h-[380px] w-full rounded-3xl object-cover" />
        <div>
          <div className="mb-3 font-mono text-xs uppercase tracking-widest text-emerald-400">{c.staff_eyebrow}</div>
          <h2 className="text-3xl font-extrabold sm:text-4xl">{c.staff_title}</h2>
          <p className="mt-4 text-slate-300">{c.staff_text}</p>
          <ul className="mt-8 space-y-4">
            {[[Smartphone, "Installs like an app on iPhone, Android and tablets"], [ClipboardCheck, "Big, tactile buttons built for busy kitchens"], [Sparkles, "Ask AI anything about food safety, using your records"], [Building2, "One account, many venues, each with its own checks"]].map(([I, t]) => (
              <li key={t} className="flex items-start gap-3 text-slate-200"><I className="mt-0.5 h-5 w-5 text-emerald-400" />{t}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Pricing({ c }) {
  return (
    <section id="pricing" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <div className="ka-eyebrow mb-3">Simple pricing</div>
          <h2 className="text-3xl font-extrabold text-slate-950 sm:text-4xl">{c.pricing_title}</h2>
          <p className="mt-4 text-slate-600">{c.pricing_text}</p>
        </div>
        <div className="rounded-3xl border-2 border-slate-950 bg-white p-8 sm:p-10">
          <div className="flex items-end gap-2"><span data-testid="pricing-amount" className="font-display text-6xl font-extrabold text-slate-950">{c.price_amount}</span><span className="mb-2 text-slate-500">{c.price_unit}</span></div>
          <p className="mt-2 text-sm text-slate-500">{c.price_note}</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {c.price_features.map((t) => <div key={t} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-emerald-600" />{t}</div>)}
          </div>
          <Link data-testid="pricing-signup-button" to="/signup" className="ka-press mt-10 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-semibold text-white hover:bg-emerald-700">Start free trial <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [raw, setC] = useState(DEFAULT_CONTENT);
  const { trial_days } = useBranding();
  const c = withTrialDays(raw, trial_days);
  useEffect(() => {
    api.get("/public/content").then((r) => setC(mergeContent(r.data))).catch(() => {}).finally(() => {
      const id = window.location.hash.slice(1);
      if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView(), 50);
    });
  }, []);
  return (
    <div className="min-h-screen bg-[#F6F7F4]">
      {c.announcement && <div data-testid="site-announcement" className="bg-emerald-700 px-5 py-2 text-center text-sm font-medium text-white">{c.announcement}</div>}
      <Nav />
      <Hero c={c} />
      <Features c={c} />
      <StaffApp c={c} />
      <Pricing c={c} />
      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-between gap-4 px-5 text-sm text-slate-500 sm:px-8">
          <span>© {new Date().getFullYear()} KitchenAudit.app</span>
          <Link data-testid="footer-tour-link" to="/tour" className="font-semibold text-slate-700 hover:underline">Product tour</Link>
          <Link data-testid="footer-demo-link" to="/demo" className="font-semibold text-emerald-700 hover:underline">Try the live demo account →</Link>
          <span>{c.footer_text}</span>
        </div>
      </footer>
    </div>
  );
}
